import type { OrderDTO } from '@wf/shared'

export type WorkshopFilter = 'alle' | 'meine' | 'hoch' | 'ueberfaellig'

export const WORKSHOP_FILTERS: { key: WorkshopFilter; label: string }[] = [
  { key: 'alle', label: 'Alle' },
  { key: 'meine', label: 'Meine' },
  { key: 'hoch', label: 'Hoch' },
  { key: 'ueberfaellig', label: 'Überfällig' },
]

/** Die vier Board-Spalten. 'abgeholt' ist bewusst keine Spalte — fertige Abholungen halten das Board nicht voll.
 *  Generisch über den Item-Typ, damit die reichen Listen-Felder (OrderListItemDTO) durch die Gruppierung fließen. */
export interface WorkshopColumns<T extends OrderDTO = OrderDTO> {
  offen: T[]
  arbeit: T[]
  wartet: T[]
  fertig: T[]
}

export const WORKSHOP_COLUMNS: { status: keyof WorkshopColumns; title: string }[] = [
  { status: 'offen', title: 'Offen' },
  { status: 'arbeit', title: 'In Arbeit' },
  { status: 'wartet', title: 'Wartet auf Teil' },
  { status: 'fertig', title: 'Fertig' },
]

const PRIO_ORDER: Record<string, number> = { hoch: 0, mittel: 1, niedrig: 2 }

function isColumn(status: string): status is keyof WorkshopColumns {
  return status === 'offen' || status === 'arbeit' || status === 'wartet' || status === 'fertig'
}

/** Lokales Datum als YYYY-MM-DD. NICHT toISOString — das liefert nach Mitternacht den UTC-Vortag (Altcode-Bug). */
export function localYmd(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Überfällig: geplanter Tag liegt vor heute und der Auftrag ist noch nicht fertig/abgeholt. */
export function isOverdue(order: OrderDTO, today: string): boolean {
  return order.scheduledDate != null && order.scheduledDate < today && order.status !== 'fertig' && order.status !== 'abgeholt'
}

export function matchesWorkshopFilter(order: OrderDTO, filter: WorkshopFilter, userId: number, today: string): boolean {
  switch (filter) {
    case 'meine':
      return order.mechanicId === userId
    case 'hoch':
      return order.prio === 'hoch'
    case 'ueberfaellig':
      return isOverdue(order, today)
    default:
      return true
  }
}

/** Gefilterte Aufträge nach Status in die Board-Spalten gruppieren, je Spalte nach Priorität (hoch zuerst). */
export function groupByColumn<T extends OrderDTO>(
  orders: readonly T[],
  filter: WorkshopFilter,
  userId: number,
  today: string,
): WorkshopColumns<T> {
  const columns: WorkshopColumns<T> = { offen: [], arbeit: [], wartet: [], fertig: [] }
  for (const order of orders) {
    if (!isColumn(order.status)) continue // abgeholt + unbekannte Status erscheinen nicht auf dem Board
    if (!matchesWorkshopFilter(order, filter, userId, today)) continue
    columns[order.status].push(order)
  }
  for (const status of ['offen', 'arbeit', 'wartet', 'fertig'] as const) {
    columns[status].sort((a, b) => (PRIO_ORDER[a.prio] ?? 1) - (PRIO_ORDER[b.prio] ?? 1))
  }
  return columns
}
